import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import useMessageActions from '~hooks/useMessaging/useMessageActions';
import MessageAttachments from '~pages/Messages/components/MessageAttachments';
import MessageBroadcastQuote from '~pages/Messages/components/MessageBroadcastQuote';
import MessageContent from '~pages/Messages/components/MessageContent';
import MessageInputShell from '~pages/Messages/components/MessageInputShell';
import MessageLengthHint from '~pages/Messages/components/MessageLengthHint';
import MessageReadReceipt from '~pages/Messages/components/MessageReadReceipt';
import MessagingAvatar, {
  MESSAGING_BUBBLE_AVATAR_SIZE_IN_PX,
} from '~pages/Messages/components/MessagingAvatar';
import {
  MESSAGING_BUBBLE_MAX_WIDTH,
  MESSAGING_BUBBLE_PADDING_X,
} from '~pages/Messages/messagingBubble';
import { isMessageTooLong } from '~pages/Messages/messagingContentLimit';
import { canDeleteMessage, canEditMessage } from '~pages/Messages/messagingMessagePolicy';
import { Nullable } from '~types/Nullable';

const MESSAGE_ACTIONS_CLASS = 'messaging-message-actions';

const MESSAGE_ROW_GAP = 1;

const RUN_INNER_CORNER_RADIUS_IN_PX = 4;

type MessageRowProps = {
  canModerate: boolean;
  channel: Nullable<MessagingChannel>;
  endsRun: boolean;
  isOwn: boolean;
  message: MessagingMessage;
  // False for the rest of a run by one sender.
  showSender: boolean;
  startsRun: boolean;
  viewerUserId: Nullable<string>;
};

export default function MessageRow({
  canModerate,
  channel,
  endsRun,
  isOwn,
  message,
  showSender,
  startsRun,
  viewerUserId,
}: MessageRowProps) {
  const theme = useTheme();
  const { deleteMessage, editMessage } = useMessageActions();

  // A message may be attachments alone, and an empty paragraph would still take up a line.
  const hasBody = message.isDeleted || (message.content ?? '').length > 0;

  const [draft, setDraft] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const canEdit = channel
    ? canEditMessage({ canModerate, channel, message, viewerUserId })
    : false;
  const canDelete = channel
    ? canDeleteMessage({ canModerate, channel, message, viewerUserId })
    : false;

  const isEditing = draft !== null;
  // Re-saving the same body would only stamp an "edited" marker on an unchanged message.
  const canSaveEdit =
    !isBusy &&
    draft !== null &&
    draft.trim().length > 0 &&
    !isMessageTooLong(draft) &&
    draft.trim() !== message.content;

  const saveEdit = useCallback(async () => {
    if (!channel || draft === null) return;

    setIsBusy(true);

    try {
      await editMessage({ channel, content: draft.trim(), message });
      setDraft(null);
    } catch {
      // The draft is kept so the edit can be retried; already toasted by the hook.
    } finally {
      setIsBusy(false);
    }
  }, [channel, draft, editMessage, message]);

  const confirmDelete = useCallback(async () => {
    if (!channel) return;

    setIsBusy(true);

    try {
      await deleteMessage({ channel, message, viewerUserId });
      setIsConfirmingDelete(false);
    } catch {
      // Reported and toasted by the hook; the dialog stays open for a retry.
    } finally {
      setIsBusy(false);
    }
  }, [channel, deleteMessage, message, viewerUserId]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDraft(null);
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();

        if (canSaveEdit) {
          void saveEdit();
        }
      }
    },
    [canSaveEdit, saveEdit],
  );

  const hasActions = (canEdit || canDelete) && !isEditing;
  const showReadReceipt = isOwn && !message.isDeleted;

  const innerCornerRadius = `${RUN_INNER_CORNER_RADIUS_IN_PX}px`;
  const runCornersSx = isOwn
    ? {
        ...(startsRun ? {} : { borderTopRightRadius: innerCornerRadius }),
        ...(endsRun ? {} : { borderBottomRightRadius: innerCornerRadius }),
      }
    : {
        ...(startsRun ? {} : { borderTopLeftRadius: innerCornerRadius }),
        ...(endsRun ? {} : { borderBottomLeftRadius: innerCornerRadius }),
      };

  const messageColumn = (
    <Box
      sx={{
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <Box
        data-test-id="messaging-message"
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: isOwn ? 'row-reverse' : 'row',
          gap: 0.5,
          ...(isEditing ? { width: '100%' } : { maxWidth: MESSAGING_BUBBLE_MAX_WIDTH }),
          // Keyboard users never hover, so focus inside the row reveals the same controls.
          [`&:hover .${MESSAGE_ACTIONS_CLASS}, &:focus-within .${MESSAGE_ACTIONS_CLASS}`]:
            { opacity: 1 },
        }}
      >
        <Box
          data-test-id="messaging-bubble"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            ...(isEditing
              ? { gap: 1, width: '100%' }
              : {
                  backgroundColor: isOwn
                    ? theme.semantic.chipInfoBg
                    : theme.semantic.surfaceRaised,
                  border: isOwn ? undefined : `1px solid ${theme.semantic.border}`,
                  borderRadius: 2,
                  ...runCornersSx,
                  gap: 0.25,
                  px: MESSAGING_BUBBLE_PADDING_X,
                  py: 1,
                }),
          }}
        >
          {isEditing ? (
            <>
              <MessageInputShell
                sx={{ backgroundColor: theme.semantic.surfaceInputSolid }}
              >
                <InputBase
                  disabled={isBusy}
                  fullWidth
                  inputProps={{ 'aria-label': t('messaging.edit_message') }}
                  maxRows={8}
                  multiline
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                  sx={{ px: 0.5 }}
                  value={draft}
                />
              </MessageInputShell>

              <MessageLengthHint content={draft} />

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <SmallButton
                  color="secondary"
                  disabled={isBusy}
                  onClick={() => setDraft(null)}
                  variant="outlined"
                >
                  {t('actions.cancel')}
                </SmallButton>
                <SmallLoadingButton
                  color="brandV2Yellow"
                  disabled={!canSaveEdit}
                  loading={isBusy}
                  onClick={() => void saveEdit()}
                >
                  {t('actions.save')}
                </SmallLoadingButton>
              </Box>
            </>
          ) : (
            <>
              {message.repliedToBroadcastChannel && (
                <MessageBroadcastQuote quote={message.repliedToBroadcastChannel} />
              )}

              {hasBody && (
                <MessageContent
                  content={message.content ?? ''}
                  isDeleted={message.isDeleted}
                  isRemovedByModerator={message.isRemovedByModerator}
                />
              )}

              {!message.isDeleted && (
                <MessageAttachments attachments={message.fileAttachments} />
              )}
            </>
          )}

          {!isEditing && (
            <Typography
              component="div"
              sx={{
                alignItems: 'center',
                alignSelf: 'flex-end',
                color: theme.semantic.textSecondary,
                display: 'flex',
                gap: 0.5,
              }}
              variant="caption"
            >
              {message.createdAt.format('h:mm A')}
              {message.isEdited ? ` · ${t('messaging.edited')}` : ''}
              {showReadReceipt && channel && (
                <MessageReadReceipt channel={channel} message={message} />
              )}
            </Typography>
          )}
        </Box>

        {hasActions && (
          <Box
            className={MESSAGE_ACTIONS_CLASS}
            sx={{
              display: 'flex',
              gap: 0.25,
              opacity: 0,
              transition: theme.transitions.create('opacity'),
              // A touch device reaches neither hover nor focus-within by tapping a row, so the
              // controls stay put rather than being unreachable.
              '@media (hover: none)': { opacity: 1 },
            }}
          >
            {canEdit && (
              <IconButton
                aria-label={t('messaging.edit_message')}
                onClick={() => setDraft(message.content ?? '')}
                size="small"
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            )}

            {canDelete && (
              <IconButton
                aria-label={t('messaging.delete_message')}
                onClick={() => setIsConfirmingDelete(true)}
                size="small"
              >
                <DeleteOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <ConfirmationDialog
        confirmButtonProps={{ children: t('actions.delete') }}
        isBusy={isBusy}
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirm={() => void confirmDelete()}
        title={t('messaging.delete_message_title')}
      >
        <Typography variant="body2">{t('messaging.delete_message_warning')}</Typography>
      </ConfirmationDialog>
    </Box>
  );

  return (
    <Box
      data-test-id="messaging-message-row"
      sx={{ display: 'flex', flexDirection: 'column', mt: startsRun ? 1.5 : 0 }}
    >
      {showSender && (
        <Typography
          sx={{
            color: theme.semantic.textSecondary,
            pl: `calc(${MESSAGING_BUBBLE_AVATAR_SIZE_IN_PX}px + ${theme.spacing(MESSAGE_ROW_GAP + MESSAGING_BUBBLE_PADDING_X)})`,
            pt: 0.5,
          }}
          variant="caption"
        >
          {message.senderName ?? t('messaging.unknown_sender')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: MESSAGE_ROW_GAP }}>
        {!isOwn && (
          <Box sx={{ flexShrink: 0, width: MESSAGING_BUBBLE_AVATAR_SIZE_IN_PX }}>
            {startsRun && (
              <MessagingAvatar
                colorKey={message.senderKey}
                name={message.senderName ?? ''}
                size={MESSAGING_BUBBLE_AVATAR_SIZE_IN_PX}
              />
            )}
          </Box>
        )}

        {messageColumn}
      </Box>
    </Box>
  );
}
